import type { MessageKey } from "./index.js";

/**
 * Sunucu hata kodu → kullanıcı mesajı anahtarı.
 *
 * ### ⚠️ Sunucunun `message` alanı ARAYÜZDE GÖSTERİLMEZ
 * Sunucu mesajı **tek dilde** üretilir, günlük/telemetri içindir ve
 * sürümler arasında değişebilir. Kullanıcıya gösterilen metin istemcinin
 * kataloğundan gelir; böylece dil seçimi ve vaat dili (Rapor 08 §9) tek
 * yerden denetlenir.
 *
 * ### ⚠️ 402 ≠ 403
 * `plan_limit_exceeded` "planını yükselt", `capability_denied` "senin rolün
 * yapamaz" der. İkisine aynı metni vermek kullanıcıya **yanlış eylem**
 * önerir (Rapor 04 §8).
 */
const BY_CODE: Readonly<Record<string, MessageKey>> = Object.freeze({
  // 401 — oturum
  token_expired: "error.sessionExpired",
  revoked: "error.sessionExpired",
  stale_epoch: "error.sessionExpired",
  invalid_ticket: "error.sessionExpired",
  missing_access_jwt: "error.sessionExpired",
  malformed_token: "error.sessionExpired",

  // 402 — plan (Rapor 13; bugün üretilmez, metin hazırdır)
  plan_limit_exceeded: "error.planLimit",

  // 403 — yetki
  capability_denied: "error.capabilityDenied",
  insufficient_rank: "error.capabilityDenied",
  self_grant_forbidden: "error.capabilityDenied",
  only_host_can_delegate_granting: "error.capabilityDenied",
  turnstile_failed: "error.capabilityDenied",
  domain_not_allowed: "error.capabilityDenied",

  // 404 / 409 / 422 — katılım akışı
  not_found: "join.notFound",
  unknown_tenant: "join.notFound",
  meeting_locked: "room.locked",
  /**
   * ⚠️ `meeting_locked`ten AYRI. Kilit GEÇİCİDİR ve host kaldırabilir; bitiş
   *    KALICIDIR ve bağlantı bir daha çalışmaz. İkisi tek koda bindirildiğinde
   *    kullanıcı "kilitlendi" okuyup bekliyor, tekrar deniyor ve sonunda
   *    "ürün bozuk" diye bildiriyordu — sistem doğru davranıyordu, yalnız
   *    yanlış şeyi söylüyordu.
   */
  meeting_is_over: "room.isOver",
  meeting_full: "join.full",
  invite_exhausted: "join.inviteExhausted",

  // 429
  rate_limited: "error.rateLimited",
  /**
   * ⚠️ `ai_quota_exceeded` ile `rate_limited` AYNI HTTP kodunu (429) paylaşır
   *    ama ayrı metinler ister: birincisinde doğru davranış **yarın** denemek,
   *    ikincisinde **birazdan** denemektir. Eşlemede yoktu; `errorKey()`
   *    konsola "eslenmemis sunucu hata kodu" yazıp `error.generic`e düşüyordu
   *    — yani kullanıcı, kotanın dolduğunu hiçbir zaman öğrenmiyordu.
   */
  ai_quota_exceeded: "assistant.quotaSpent",
  ticket_quota_exceeded: "error.rateLimited",

  /**
   * 409 / 500 — medya yolu.
   *
   * 🚨 Bu kodların HİÇBİRİ eşlenmemişti ve hepsi `error.generic`e düşüyordu.
   *    İngilizce yerelde o metin **"Something went wrong."**tur — katılımcının
   *    ekranında gördüğü tam olarak buydu. Yani sorun kullanıcıya "bilinmeyen
   *    bir şey oldu" diye görünürken sunucu tarafında kodu belliydi.
   */
  duplicate_publish_session: "error.rejoinConflict",
  authorization_changed: "error.sessionExpired",
  track_not_in_meeting: "error.network",
  force_muted: "room.forcedMuteMic",
  internal_error: "error.generic",

  /**
   * ⭐ **WEBSOCKET KAPISININ RET KODLARI** — bunlar da eşlenmemişti.
   *
   * 🚨 `webSocketMessage()` beş kapıdan geçer ve her kapının kendi kodu vardır.
   *    Hiçbiri katalogda yoktu; hepsi `error.generic`e düşüyordu. İngilizce
   *    yerelde o metin **"Something went wrong."**tur — katılımcının ekranında
   *    gördüğü tam olarak buydu (30 Tem 2026 turu). Sunucu hangi kapının
   *    kapandığını **biliyordu**; kullanıcıya söylenmiyordu.
   *
   * ⚠️ `bad_frame` / `bad_json` / `bad_payload` kullanıcının hatası DEĞİLDİR:
   *    istemci ile sunucu sürümleri uyuşmuyor demektir. Doğru eylem "yeniden
   *    yükle"dir, "daha dikkatli ol" değil.
   */
  bad_frame: "error.clientOutdated",
  bad_json: "error.clientOutdated",
  bad_payload: "error.clientOutdated",
  bad_track_count: "error.clientOutdated",
  mixed_locations: "error.clientOutdated",
  in_lobby: "lobby.waiting",
  no_such_participant: "error.staleView",
  not_active: "error.sessionExpired",

  // 502 — yukarı akış
  sfu_error: "error.network",
  ai_error: "error.generic",
  upstream_error: "error.network",
  api_origin_misconfigured: "error.network",
});

/**
 * Bilinmeyen kod **sessizce yutulmaz**: genel hata metni gösterilir.
 *
 * ⚠️ **Ham kod konsola da yazılır.** "Beklenmeyen bir hata oluştu" kullanıcı
 *    için doğru cevaptır ama arıza turunda **hiçbir işe yaramaz**: geçen turda
 *    katılımcının gördüğü "Something went wrong." metninin arkasındaki kodu
 *    bulmak, sunucu günlüğünü satır satır okumayı gerektirdi. Kod artık
 *    istemcide de görünür; eşlenmemiş her yeni sunucu kodu bir sonraki turda
 *    kendini gösterir.
 */
export function errorKey(code: string | undefined): MessageKey {
  if (code === undefined) return "error.generic";
  const mapped = BY_CODE[code];
  if (mapped) return mapped;
  console.warn(`[centvox] eslenmemis sunucu hata kodu: ${code}`);
  return "error.generic";
}
